/** Pure Memberships (اعضای سازمان) display mapping — no I/O. D5A. */

import type { StatusTone } from "./overview-ops";

export type MembershipListRow = {
  id: string;
  organizationId: string;
  userId: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  userActive: boolean;
  invitedAt: string | null;
  activatedAt: string | null;
  updatedAt: string;
};

export type MembershipSortKey = "name" | "email" | "role" | "status" | "updated";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export const MEMBERSHIPS_PAGE_LABEL = "اعضای سازمان";

export const MEMBERSHIPS_PAGE_NOTE =
  "اعضای سازمان فهرست عضویت‌های سازمان فعال را نشان می‌دهد. وضعیت عضویت با وضعیت حساب ورود فرق دارد. رمز، توکن دعوت، راز MFA، یا دادهٔ نشست هرگز در فهرست یا جزئیات نمایش داده نمی‌شود.";

export const MEMBERSHIP_ROLE_LABELS: Record<string, string> = {
  tenant_admin: "مدیر سازمان",
  publisher: "ناشر",
  editor: "ویرایشگر",
  auditor: "ممیز",
};

export const MEMBERSHIP_STATUS_LABELS: Record<string, string> = {
  invited: "دعوت‌شده",
  active: "فعال",
  suspended: "معلق",
  removed: "حذف‌شده",
};

export function membershipRoleLabel(role: string): string {
  return MEMBERSHIP_ROLE_LABELS[role] ?? role;
}

export function membershipRoleDisplay(role: string): StatusDisplay {
  const label = membershipRoleLabel(role);
  if (role === "tenant_admin") {
    return { label, tone: "warning", detail: "نقش فنی: tenant_admin" };
  }
  if (role === "auditor") {
    return { label, tone: "neutral", detail: `نقش فنی: ${role}` };
  }
  return { label, tone: "info", detail: `نقش فنی: ${role}` };
}

export function membershipStatusDisplay(status: string): StatusDisplay {
  const label = MEMBERSHIP_STATUS_LABELS[status] ?? status;
  if (status === "active") {
    return { label, tone: "success", detail: "عضویت در سازمان فعال است" };
  }
  if (status === "invited") {
    return {
      label,
      tone: "warning",
      detail: "دعوت در انتظار فعال‌سازی — هنوز عضویت فعال نیست",
    };
  }
  if (status === "suspended") {
    return { label, tone: "danger", detail: "عضویت معلق است" };
  }
  if (status === "removed") {
    return { label, tone: "neutral", detail: "عضویت حذف شده است" };
  }
  return { label, tone: "neutral" };
}

/** Account login status — distinct from organization membership status. */
export function accountStatusDisplay(userActive: boolean): StatusDisplay {
  return userActive
    ? { label: "حساب فعال", tone: "success", detail: "وضعیت حساب ورود — جدا از وضعیت عضویت" }
    : {
        label: "حساب غیرفعال",
        tone: "neutral",
        detail: "حساب ورود غیرفعال است — جدا از وضعیت عضویت سازمان",
      };
}

export function invitationStateDisplay(row: MembershipListRow): StatusDisplay {
  if (row.status === "invited") {
    return {
      label: "دعوت در انتظار",
      tone: "warning",
      detail: "فعال‌سازی لازم است تا عضویت فعال شود",
    };
  }
  if (row.activatedAt) {
    return {
      label: "عضویت فعال‌شده",
      tone: "success",
      detail: "دعوت تکمیل یا عضویت قبلاً فعال شده",
    };
  }
  return { label: "بدون دعوت باز", tone: "neutral" };
}

export function matchesMembershipQuery(row: MembershipListRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.displayName,
    row.email,
    row.role,
    membershipRoleLabel(row.role),
    row.status,
    MEMBERSHIP_STATUS_LABELS[row.status] ?? "",
    row.id,
    row.userId,
    row.userActive ? "حساب فعال" : "حساب غیرفعال",
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesMembershipFilter(row: MembershipListRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "invited") return row.status === "invited";
  if (filter === "active") return row.status === "active";
  if (filter === "suspended") return row.status === "suspended";
  if (filter === "removed") return row.status === "removed";
  if (filter === "account_inactive") return !row.userActive;
  if (filter === "tenant_admin" || filter === "publisher" || filter === "editor" || filter === "auditor") {
    return row.role === filter;
  }
  return true;
}

export function filterAndSortMemberships(
  rows: MembershipListRow[],
  opts: { query: string; filter: string; sort: MembershipSortKey },
): MembershipListRow[] {
  const filtered = rows.filter(
    (r) => matchesMembershipQuery(r, opts.query) && matchesMembershipFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "email") return a.email.localeCompare(b.email, "en");
    if (opts.sort === "role") return a.role.localeCompare(b.role);
    if (opts.sort === "status") return a.status.localeCompare(b.status);
    if (opts.sort === "updated") return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
    return a.displayName.localeCompare(b.displayName, "fa");
  });
  return sorted;
}

export type RelatedMembershipsLink = { id: string; label: string; to: string };

export function relatedMembershipsLinks(): RelatedMembershipsLink[] {
  return [
    { id: "entitlements", label: "دسترسی پلان", to: "/entitlements" },
    { id: "users", label: "کاربران و نقش‌ها", to: "/users" },
    { id: "audit", label: "سابقه تغییرات", to: "/audit" },
    { id: "overview", label: "نمای کلی", to: "/" },
  ];
}
